import { Trans } from "@lingui/react/macro"
import { Alert, Stack, Text } from "@mantine/core"
import { testAdminLLMProvider } from "../../../../lib/api"
import { ProviderCard } from "../ProviderCard"

interface IProps {
  settings: Record<string, string>
  /** Secret keys reported as already-configured (masked) by the backend. */
  configuredSecrets: Set<string>
  saving: boolean
  isAdminOnly: boolean
  updateSetting: (key: string, value: string) => void
  saveKey: (key: string) => void
}

export function LlmProvidersPanel({
  settings,
  configuredSecrets,
  saving,
  isAdminOnly,
  updateSetting,
  saveKey,
}: IProps): React.JSX.Element {
  // Platform provider keys are super-admin-only (the backend PUT rejects a tenant
  // write with a 403). Don't render dead credential cards to a tenant — show a
  // pointer instead. (There is no org-BYOK LLM card today; when one lands it
  // belongs here for tenants.)
  if (!isAdminOnly) {
    return (
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>AI provider keys are managed by your platform administrator.</Trans>
        </Text>
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>
            Configure API keys for AI providers. Companies can choose to use these platform keys or provide their own
            (BYOK).
          </Trans>
        </Text>
      </Alert>
      <ProviderCard
        name="anthropic"
        label="Anthropic (Claude)"
        currentKey={settings.platform_anthropic_key ?? ""}
        configured={configuredSecrets.has("platform_anthropic_key")}
        onKeyChange={(v) => updateSetting("platform_anthropic_key", v)}
        onSave={() => saveKey("platform_anthropic_key")}
        saving={saving}
        onTestOverride={(p, k) => testAdminLLMProvider(p, k)}
      />
      <ProviderCard
        name="openrouter"
        label="OpenRouter"
        currentKey={settings.platform_openrouter_key ?? ""}
        configured={configuredSecrets.has("platform_openrouter_key")}
        onKeyChange={(v) => updateSetting("platform_openrouter_key", v)}
        onSave={() => saveKey("platform_openrouter_key")}
        saving={saving}
        onTestOverride={(p, k) => testAdminLLMProvider(p, k)}
      />
      <ProviderCard
        name="openai"
        label="OpenAI"
        currentKey={settings.platform_openai_key ?? ""}
        configured={configuredSecrets.has("platform_openai_key")}
        onKeyChange={(v) => updateSetting("platform_openai_key", v)}
        onSave={() => saveKey("platform_openai_key")}
        saving={saving}
        onTestOverride={(p, k) => testAdminLLMProvider(p, k)}
      />
    </Stack>
  )
}
